import type { Logger } from "../../logger";
import type { Http, HttpConfig } from "../http.contract";

import { CONFIG, type Config } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";
import { LOGGER } from "../../logger";
import { NoResponseError } from "../errors/no-response.error";
import { ServerError } from "../errors/server.error";
import { HttpMethod } from "../http.contract";
import { HTTP } from "../http.types";
@Injectable(HTTP)
export class FetchService implements Http {
  private readonly logger: Logger;

  constructor(
    @Inject(CONFIG) private config: Config,
    @Inject(LOGGER) private loggerFactory: Logger
  ) {
    this.logger = this.loggerFactory.create("Http");
  }

  async isPerimeterexError(error: unknown): Promise<boolean> {
    if (!error || typeof (error as Response).status !== "number") {
      return false;
    }

    const response = error as Response;

    if (response.status !== 403) {
      return false;
    }

    // Tenta verificar headers comuns do PerimeterX
    const hasPxHeaders = [...response.headers.entries()].some(([key]) => {
      return (
        key.toLowerCase().includes("perimeterx") ||
        key.toLowerCase().startsWith("px-")
      );
    });

    // Verifica o corpo da resposta buscando padrões do PerimeterX
    try {
      const contentType = response.headers.get("content-type");
      if (contentType?.includes("application/json")) {
        const body = await response.clone().json();

        const hasPxBody =
          typeof body === "object" &&
          body !== null &&
          "appId" in body &&
          "uuid" in body;

        return hasPxHeaders || hasPxBody;
      }
    } catch (e) {
      this.logger.warn(
        "[isPerimeterexError] Erro ao analisar corpo da resposta:",
        e
      );
    }

    return hasPxHeaders;
  }

  async request(url: string, config?: HttpConfig): Promise<string> {
    const headers = new Headers(config?.headers);
    const params = new URLSearchParams(
      config?.params as Record<string, string>
    ).toString();
    const requestURL = `${url}?${decodeURIComponent(params)}`;

    const options: RequestInit = {
      method: config?.method ?? HttpMethod.GET,
      headers: this.config.disabledHeaders ? {} : headers,
      mode: "no-cors",
      ...config,
    };

    this.logger.info("[request] sending request: ", requestURL);
    this.logger.info("[request] request options: ", options);

    try {
      const response = await fetch(requestURL, options);

      this.logger.info("[request] response status text: ", response.statusText);
      this.logger.info("[request] response status code: ", response.status);
      this.logger.info("[request] response: ", response);

      if (await this.isPerimeterexError(response)) {
        this.logger.error("[request] PerimeterX detected. Access denied.");

        throw response;
      }

      if (!response.ok) {
        const errorData = await response.text();

        this.logger.error("[request] something went wrong", errorData);

        throw new ServerError(errorData);
      }

      return response.text();
    } catch (error: any) {
      this.logger.error("[request] something went wrong", error);

      if (error instanceof ServerError) {
        throw error;
      }
      if (!error?.response) {
        throw new NoResponseError("No response received");
      }
      throw error;
    }
  }
}
